import React, { useEffect, useState } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";
import ReactTooltip from "react-tooltip";
import PhoneNumberInput from "react-phone-number-input";
import { libphonenumberFilteredMetadata } from "./twilio-geo-permissions";
import { getCountryCallingCode, parsePhoneNumberFromString } from "libphonenumber-js"
import { Field } from "redux-form";
import { Link, withRouter } from "react-router-dom";
import Step from "../Step";

import styles from "../styles.module.css";

import WithUserForm from "../../Register/WithUserForm";
import {
  required,
  email,
  phoneNumber,
  nonSpecialChar,
  nonStartWithSpace,
} from "src/utils/validate";
import { HomeCityField } from "./cities-auto-complete";

const ERROR_EMPTY = "All fields are required, darling!";


const Input = ({ input, placeholder, inputClass, placeholderClass, redStarClass, ...props }) => {
  useEffect(() => {
    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.meta.touched, input.value]);

  return (
      <input
          className={`${styles.input} ${styles.default_text_size} ${props.meta.error &&
          props.meta.touched &&
          styles.error_input} ${inputClass}`}
          {...input}
          id={input.name}
          placeholder={placeholder}
      />
  );
};

const PhoneNumberField = ({ input, phoneNumberValue, ...props }) => {
  const [isClear, setIsClear] = useState(false);

  useEffect(() => {
    if (props.isEmpty) {
      setIsClear(true);
      props.handleErrors({
        name: input.name,
        error: ERROR_EMPTY,
      });
      return;
    }

    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.isEmpty, props.meta.touched]);

  return (
      <PhoneNumberInput
          {...input}
          {...props}
          value={phoneNumberValue}
          className={`${styles.combobox_phone_number} ${styles.default_text_size} ${(props.meta.error ||
              props.isEmpty) &&
          props.meta.touched &&
          styles.error_input_phone}`}
      />
  );
};

const AcceptedTosInput = ({ input, ...props }) => {
  useEffect(() => {
    if (props.meta.touched) {
      props.handleErrors({
        name: input.name,
        error: props.meta.error,
      });
    }
  }, [props.meta.error, props.meta.touched]);

  return (
      <label className={styles.wrap_checkbox}>
        <input
            type="checkbox"
            {...input}
            {...props}
            checked={input.value}
        />
        <span className={styles.check_mark} />
      </label>
  );
};

export const CountryCode = ({ country }) => (
  <span style={{ color: "white" }}>+{getCountryCallingCode(country)}</span>
);


class StepFirstClass {
  errors = [];

  setErrors(error) {
    this.errors = this.errors.concat(error);
  }
}

const stepFirstClass = new StepFirstClass();

class StepFirst extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errors: [],
      isEmpty: false,
      isRequired: true,
      isFirstTime: true,
      indexChange: 0,
      phoneNumber: '',
    };
  }

  removeSubmitErrors = () => {
    const errors = this.state.errors.filter(err => err.name !== "submit");
    this.setState({ errors });
  };

  handleChange = (value) => {
    this.removeSubmitErrors()
    if (!value) {
      this.setState({
        phoneNumber: '',
        isEmpty: true,
        isRequired: true
      });
    } else {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed?.isValid() ) {
        const localNumber = parsed.nationalNumber;
        const formatted = parsed.country ? `+${getCountryCallingCode(parsed.country)} ${localNumber}` : value;
        this.setState({ phoneNumber: formatted });
      } else {
        this.setState({ phoneNumber: value });
      }
      this.setState({
        isEmpty: false,
        isRequired: false
      });
    }
  };

  handleErrors = (error) => {
    let errors = [...this.state.errors];

    if (errors.length > 0) {
      const isExist = errors.some((err) => err.name === error.name);
      if (isExist) {
        const index = errors.findIndex((err) => err.name === error.name);
        if (!error.error) {
          errors.splice(index, 1);
          this.setState({ indexChange: 0 });
        } else {
          errors[index].error = error.error;
          this.setState({ indexChange: index });
        }
      } else {
        if (error.error) {
          errors.push(error);
          this.setState({ indexChange: errors.length - 1 });
        }
      }
    } else {
      if (error.error) {
        errors.push(error);
      }
    }

    if (this.state.isFirstTime && error.error) {
      stepFirstClass.setErrors(error);
      errors = stepFirstClass.errors;
      this.setState({ isFirstTime: false });
    }

    errors = [...new Map(errors.map((err) => [err["name"], err])).values()];

    this.setState({
      errors,
    });
  };

  isErrorAcceptedTos = () => {
    if (this.state.errors.find((err) => err.name === "accepted_tos")) {
      return true;
    }
    return false;
  };

  async onSubmit(values, dispatch, props, self) {
    values = Object.assign({}, values);
    try {
      if (this.state.isEmpty) {
        return;
      }

      // trigger recaptcha
      let token = null;
      if (process.env.REACT_APP_RECAPTCHA_ENABLED === 'true') {
        token = await this.props.recaptchaRef.current.executeAsync();
        this.props.recaptchaRef.current.reset();
      }
      const formattedValues = WithUserForm.formatUserGeneralInformationInput(values);
      const updateUserGeneralInformationResult = await props.updateUserGeneralInformation({
        variables: {
          input: formattedValues,
          recaptchaToken: token
        },
      });
      window.localStorage.setItem("token", updateUserGeneralInformationResult.data.updateUserGeneralInformation.auth.token)
      props.machine.service.send({
        type: "NEXT",
      });
      await props.userQuery.refetch();
    } catch (e) {
      if (e.graphQLErrors) {
        // JWT Token expired, remove it from local storage
        if (e.graphQLErrors[0]?.extensions?.reason === 'JWT_EXPIRED') {
          localStorage.removeItem("token");
        }
        else {
          this.handleErrors({
            name: "submit",
            error: e.graphQLErrors[0].message,
          });
        }
      }
    }
  }
  customMetadata = libphonenumberFilteredMetadata();

  render() {
    return (
      <WithUserForm
        formRequiresAuthentication={false}
        onSubmit={(values, dispatch, props) =>
            this.onSubmit(values, dispatch, props, this)
        }
      >
        {({form: {handleSubmit, anyTouched, invalid}, userQuery: {data}}) => {
          const footer = (
              <button
                  onClick={handleSubmit}
                  className={`${styles.first_page_next}`}
                  disabled={this.state.errors.length > 0 || !anyTouched || invalid}
              >
                Next
              </button>
          );
          return (<Step footer={footer}>
            {anyTouched && this.state.errors && this.state.errors.length > 0 && this.state.errors.some(err => err.name === "submit") ? (
              <p className={styles.sub_title} style={{color: '#e4293a'}}>
                You previously created an account.<br />
                <Link to="/login" className={styles.sub_title_link} style={{display: 'inline', textDecoration: 'underline', color: '#e4293a', fontSize: '12px'}}>Click here</Link> to continue your application.
              </p>
            ) : (
              <p style={{
                color: "#B08D57",
                textAlign: 'center',
                marginBottom: "10px",
                fontSize: 'clamp(9px, 2vh, 12px)'
              }}>
                If you previously created an account and<br /> wish to continue your application, <Link to="/login" style={{display: 'inline', textDecoration: 'underline'}}>click here</Link>
              </p>
            )}
            <form
              onSubmit={handleSubmit}
                style={{
                  width: '100%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
            >
              <Field
                component={Input}
                validate={[required, nonSpecialChar, nonStartWithSpace]}
                name="firstName"
                placeholder="First name"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
              />
              <Field
                name="lastName"
                component={Input}
                validate={[required, nonSpecialChar, nonStartWithSpace]}
                placeholder="Family name"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
              />
              <Field
                name="phoneNumber"
                component={PhoneNumberField}
                validate={[required, phoneNumber]}
                placeholder="Phone"
                country={"GB"}
                limitMaxLength={true}
                flagComponent={({country}) => (
                    <CountryCode country={country}/>
                )}
                metadata={this.customMetadata}
                international={false}
                displayInitialValueAsLocalNumber={true}
                type="tel"
                onChange={(value) => this.handleChange(value)}
                phoneNumberValue={this.state.phoneNumber}
                handleErrors={this.handleErrors}
                errors={this.state.errors}
                isEmpty={this.state.isEmpty}
                isRequired={this.state.isRequired}
                data={data && data.currentUser}
              />
              <Field
                name="email"
                component={Input}
                validate={[required, email]}
                placeholder="Email"
                type="email"
                handleErrors={this.handleErrors}
                errors={this.state.errors}
                onChange={this.removeSubmitErrors}
              />
              <div className={`${styles.instagram_input}`}>
                <Field
                    name="homeCity"
                    component={HomeCityField}
                    validate={[required]}
                    placeholder="Enter your home-base city"
                    handleErrors={this.handleErrors}
                    errors={this.state.errors}
                />
                <div className={styles.tool_tip}>
                  <div
                      data-tip
                      data-for="homeCityTip"
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                  <ReactTooltip
                      id="homeCityTip"
                      border={true}
                      borderColor={"#979797"}
                      backgroundColor={"#000000"}
                      effect="solid"
                      multiline={true}
                      clickable={true}
                      className={styles.description_tooltip}
                  >
                    <p>
                        This is the city where you are based, where you have your home & closet and where you are most likely to be available for work without the need for travel.
                    </p>
                  </ReactTooltip>
                </div>
              </div>

              <div className={`${styles.instagram_input}`}>
                <Field
                  name="signupInstagramHandle"
                  component={Input}
                  validate={[required, nonStartWithSpace]}
                  placeholder="Your main Instagram"
                  type="text"
                  handleErrors={this.handleErrors}
                  errors={this.state.errors}
                  onChange={this.removeSubmitErrors}
                />
                <div className={styles.tool_tip}>
                  <div
                      onClick={e => e.preventDefault()}
                      data-tip
                      data-for="instagramSelfTip"
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                  <ReactTooltip
                      id="instagramSelfTip"
                      border={true}
                      borderColor={"#979797"}
                      backgroundColor={"#000000"}
                      effect="solid"
                      multiline={true}
                      clickable={true}
                      className={styles.description_tooltip}
                  >
                    <p>
                      This is the Instagram account you use to promote yourself and your brand. Your application will be reviewed based on this Instagram account and content.
                    </p>
                  </ReactTooltip>
                </div>
              </div>
              <div className={`${styles.instagram_input}`}>
                <Field
                    name="referrer"
                    component={Input}
                    validate={[required, nonStartWithSpace]}
                    placeholder="Referring member’s Instagram"
                    type="text"
                    handleErrors={this.handleErrors}
                    errors={this.state.errors}
                />
                <div className={styles.tool_tip}>
                  <div
                      onClick={e => e.preventDefault()}
                      data-tip
                      data-for="registerTip"
                  >
                    <AiOutlineQuestionCircle size={18}/>
                  </div>
                  <ReactTooltip
                      id="registerTip"
                      border={true}
                      borderColor={"#979797"}
                      backgroundColor={"#000000"}
                      effect="solid"
                      multiline={true}
                      clickable={true}
                      className={styles.description_tooltip}
                  >
                    <p>
                      This is the Instagram account of the Schmodel member who is referring you to join. All applicants must be referred by an existing member.
                    </p>
                  </ReactTooltip>
                </div>
              </div>
              <div
                className={`${styles.confirm_block_container} ${this.isErrorAcceptedTos() && styles.error_input}`}
              >
                <Field
                  name="accepted_tos"
                  normalize={(v) => !!v}
                  format={(v) => !!v}
                  component={AcceptedTosInput}
                  validate={[required]}
                  handleErrors={this.handleErrors}
                />

                <div className={`${styles.text} ${styles.default_text_size}`}>
                  By ticking this box and clicking next,
                  I confirm that I agree to the
                  <p className={styles.text_span} >
                    <Link to="/terms-and-conditions" style={{textDecoration: 'underline'}}>
                      T&C's
                    </Link>
                    ,{" "}
                    <Link to="/privacy-notice" style={{textDecoration: 'underline'}}>
                      Privacy Notice
                    </Link>{" "}
                    &{" "}
                    <Link to="/cookie-statement" style={{textDecoration: 'underline'}}>
                      Cookies
                    </Link>
                  </p>
                </div>
              </div>
            </form>
          </Step>);
        }}
      </WithUserForm>
    );
  }
}

StepFirst = withRouter(StepFirst);

export default StepFirst;
